import { authenticatedClient } from '../lib/client';
import { ASSISTANT_ENDPOINTS } from './assistants.utils';
import type { ApiResponse } from '../lib/types';
import type { Assistant, AssistantList, AssistantVoiceOption, LanguageOption } from './assistants.types';

export const getAssistants = async (): Promise<ApiResponse<AssistantList>> => {
    try {
        const response = await authenticatedClient.get(ASSISTANT_ENDPOINTS.GET_ALL);
        // console.log("Assistants:", response.data);
        return { data: response.data };
    } catch (error: any) {
        // Include status code in error message for better error handling
        const statusCode = error.response?.status;
        const message = error.response?.data?.message || 'Failed to fetch assistants';
        return { error: statusCode ? `${statusCode}: ${message}` : message };
    }
};

export const getAssistantById = async (assistantId: string): Promise<ApiResponse<Assistant>> => {
    try {
        const response = await authenticatedClient.get(ASSISTANT_ENDPOINTS.GET_BY_ID(assistantId));
        // console.log("Assistant:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch assistant' };
    }
};

export const createAssistant = async (payload: Partial<Assistant>): Promise<ApiResponse<Assistant>> => {
    try {
        // console.log('Creating assistant with payload:', payload);
        const response = await authenticatedClient.post(ASSISTANT_ENDPOINTS.CREATE, payload);
        // console.log("Created assistant:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to create assistant' };
    }
};

export const updateAssistant = async (
    assistantId: string,
    payload: Partial<Assistant>
): Promise<ApiResponse<Assistant>> => {
    try {
        // console.log('Updating assistant with payload:', JSON.stringify(payload, null, 2));
        const response = await authenticatedClient.patch(ASSISTANT_ENDPOINTS.UPDATE(assistantId), payload);
        // console.log("Updated assistant response:", response.data);
        return { data: response.data };
    } catch (error: any) {
        // console.error('Update assistant error:', error);
        // console.error('Error response:', error.response?.data);
        
        // Check for detailed error message in the response
        if (error.response?.data?.detail) {
            return { error: error.response.data.detail };
        }
        
        return { error: error.response?.data?.message || 'Failed to update assistant' };
    }
};

export const deleteAssistant = async (assistantId: string): Promise<ApiResponse<{ success: boolean }>> => {
    try {
        await authenticatedClient.delete(ASSISTANT_ENDPOINTS.DELETE(assistantId));
        return { data: { success: true } };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to delete assistant' };
    }
};

export const getAssistantVoices = async (): Promise<ApiResponse<AssistantVoiceOption[]>> => {
    try {
        const response = await authenticatedClient.get<AssistantVoiceOption[]>(ASSISTANT_ENDPOINTS.GET_VOICES);
        // console.log("Assistant voices:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch assistant voices' };
    }
};

export const getAssistantLanguages = async (): Promise<ApiResponse<LanguageOption[]>> => {
    try {
        const response = await authenticatedClient.get<LanguageOption[]>(ASSISTANT_ENDPOINTS.GET_LANGUAGES);
        // console.log("Assistant languages:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch assistant languages' };
    }
};